import React, { useState, Suspense, lazy } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useGetConnectionsQuery } from '../../../../../features/connections/connectionsApi'
import { paths } from '../../../../../app/routes'
import { CONNECTION_STATUS, connectionStatus, summarizeConnections } from '../../../../../features/connections/connectionStatus'
import PageLoader from '../../../../SharedComponents/PageState/PageLoader'
import HmyProducts from '../../../../Headings/HmyProducts'
import NewConnector from '../../../../Modules/NewConnector/NewConnector'
import * as NO_CONNECTORS from '../../../../Constants/Constants'
import InfoComponent from '../../../../Alerts/InfoComponent/InfoComponent'
import ConnectorCardSkeleton from './ConnectorCardSkeleton'

const ConnectorListing = lazy(() => import('../../../Dashboard/ConnectorPanel/Connectors/ConnectorListing/ConnectorListing'))

const STATUS_FILTERS = Object.values(CONNECTION_STATUS)

const Connectors = () => {
    const history = useHistory()
    const location = useLocation()
    const { data: connectors = [], isLoading, isSuccess } = useGetConnectionsQuery()
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState(() => {
        const status = new URLSearchParams(location.search).get('status')
        return STATUS_FILTERS.includes(status) ? status : 'all'
    })
    const [viewMode, setViewMode] = useState('grid')

    const filteredConnectors = connectors.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(search.trim().toLowerCase())
        const matchesStatus = statusFilter === 'all' || connectionStatus(item) === statusFilter
        return matchesSearch && matchesStatus
    })

    const summary = summarizeConnections(connectors)

    function selectConnector(index){
        const connector = filteredConnectors[index]
        if (connector) history.push(paths.productDetail(connector.id))
    }

    return (
        <>
            <div className="mp-page-header">
                <div className="mp-page-heading">
                    <h1 className="mp-page-title"><HmyProducts /></h1>
                    <p className="mp-page-subtitle">Manage and monitor your connectors, pipelines and integrations</p>
                </div>
                <div className="mp-page-controls">
                    <div className="mp-search">
                        <i className="uil-search" aria-hidden="true"></i>
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="mp-status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        aria-label="Filter by status"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="expiring">Expiring Soon</option>
                        <option value="expired">Expired</option>
                    </select>
                    <div className="mp-view-toggle" role="group" aria-label="View mode">
                        <button type="button" className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>Grid</button>
                        <button type="button" className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>List</button>
                    </div>
                    <NewConnector />
                </div>
            </div>
            {
                connectors.length > 0 &&
                <div className="mp-summary-row">
                    <div className="mp-summary-item">
                        <span className="mp-summary-value">{summary.total}</span>
                        <span className="mp-summary-label">Total Products</span>
                    </div>
                    <div className="mp-summary-item">
                        <span className="mp-summary-value mp-text-success">{summary.active}</span>
                        <span className="mp-summary-label">Active</span>
                    </div>
                    <div className="mp-summary-item">
                        <span className="mp-summary-value mp-text-warning">{summary.expiring}</span>
                        <span className="mp-summary-label">Expiring Soon</span>
                    </div>
                    <div className="mp-summary-item">
                        <span className="mp-summary-value mp-text-danger">{summary.expired}</span>
                        <span className="mp-summary-label">Expired</span>
                    </div>
                </div>
            }
            {
                connectors.length > 0 ?
                    filteredConnectors.length > 0 ?
                        <div className='row'>
                            <div className='col-12'>
                                <div className='card nav-pills no-border p-0 no-boxshadow mb-0' style={{ backgroundColor: "transparent" }}>
                                    <div className="mp-connector-scroll">
                                        <div className={viewMode === 'list' ? 'connector-grid connector-list' : 'connector-grid'}>
                                            <Suspense fallback={filteredConnectors.map((item) => <ConnectorCardSkeleton key={item.id} />)}>
                                                {
                                                    filteredConnectors.map((item,index) =>
                                                        <ConnectorListing key={item.id}
                                                            connectorIndex={index}
                                                            name={item.name}
                                                            sourceShortName={item.source.shortName}
                                                            targetShortName={item.target.shortName}
                                                            isSetupCompleted={item.instances[0].isSetupCompleted}
                                                            expiryDate={item.expiryDate}
                                                            errorCount={item.instances[0].errorCount}
                                                            entityCount={item.instances[0].entityCount}
                                                            selectedIndex={-1}
                                                            onSelect={selectConnector}
                                                            onViewDetail={selectConnector}
                                                            showActiveBadge
                                                        />
                                                    )
                                                }
                                            </Suspense>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    :
                        <div className="mp-empty-state">No products match your search or filter.</div>
                : isLoading ?
                    <PageLoader message="Loading products..." />
                : isSuccess ?
                    <div id='mainWarningInfo' className='row'>
                        <div className='col-md-12'>
                            <InfoComponent info={NO_CONNECTORS.NO_CONNECTORS} />
                        </div>
                    </div>
                : null
            }
        </>
    )
}

export default Connectors
